<template>
  <div class="application-component feature-breakdown-component">
    <div class="application-component-title feature-title">
      <span>特征拆解</span>
      <el-button v-if="!readOnly && items.length" size="small" @click="handleAdd">新增</el-button>
    </div>
    <div
      class="application-component-content"
      v-loading="!readOnly && generating"
      element-loading-text="正在思考中..."
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="5" animated />
      </div>
      <template v-else>
        <div v-if="items.length" class="feature-table" :style="{ fontSize }">
          <div class="feature-table-head">
            <div class="header-cell feature-code-col">编号</div>
            <div class="header-cell feature-content-col">本交底技术特征</div>
            <div class="header-cell feature-action-col">操作</div>
          </div>

          <div class="feature-table-body">
            <div
              v-for="(element, index) in items"
              :key="element.id"
              class="feature-row"
              :class="{ active: activeItemId === element.id }"
              @click="handleSelect(element)"
            >
              <div class="feature-code-col feature-code-value">{{ element.code }}</div>
              <div class="feature-content-col feature-content-value">
                <el-input
                  v-model="element.content"
                  type="textarea"
                  :autosize="{ minRows: 2, maxRows: 4 }"
                  @focus="handleSelect(element)"
                  @blur="handleContentBlur"
                />
                <div class="feature-source" :class="{ empty: !element.sourceRanges?.length }">
                  {{ buildSourceSummary(element) }}
                </div>
              </div>
              <div class="feature-action-col feature-action-list" @click.stop>
                <button class="feature-action" type="button" @click="handleMove(index, -1)">上移</button>
                <button class="feature-action" type="button" @click="handleMove(index, 1)">下移</button>
                <button class="feature-action danger" type="button" @click="handleDelete(index)">删除</button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="feature-empty">
          <el-empty description="暂无特征拆解结果">
            <el-button type="primary" size="small" :loading="generating" @click="handleGenerate">
              AI生成
            </el-button>
          </el-empty>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, onUnmounted, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import { fetchGenerateFeatureBreakdown, fetchSaveComponent } from '@/api/writing'
import { jsonParse } from '@/utils'

const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'
const ANNOTATION_COMPONENT_CODE = 'comp_ann'
const componentTypeCode = 'comp_fea'

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null,
  },
  projectId: {
    type: String,
    default: ''
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null
  },
})

const items = ref([])
const activeItemId = ref('')
const generating = ref(false)
const localExtOne = ref('')
const lastAutoGenerateKey = ref('')

const fontSize = computed(() => `${globalStore.fontSize}px`)
const annotationRaw = computed(() => writingStore.step2Info?.step2Component?.[ANNOTATION_COMPONENT_CODE]?.docResult || '')
const hasAnnotationContent = computed(() => {
  const parsed = jsonParse(annotationRaw.value, [])
  if (!Array.isArray(parsed)) {
    return false
  }

  return parsed.some(item => item?.type === 'TEXT' && String(item?.text || '').trim())
})
const hasFeatureContent = computed(() => items.value.some(item => String(item?.content || '').trim()))

const normalizeSourceRanges = (sourceRanges = []) => {
  if (!Array.isArray(sourceRanges)) {
    return []
  }

  return sourceRanges
    .map((item) => {
      const paragraphIndex = Number(item?.paragraphIndex)
      const startOffset = Number(item?.startOffset)
      const endOffset = Number(item?.endOffset)

      return {
        paragraphIndex: Number.isFinite(paragraphIndex) ? paragraphIndex : null,
        anchorId: item?.anchorId || (Number.isFinite(paragraphIndex) ? `note-paragraph-${paragraphIndex}` : ''),
        startOffset: Number.isFinite(startOffset) ? startOffset : null,
        endOffset: Number.isFinite(endOffset) ? endOffset : null,
        quote: item?.quote || '',
      }
    })
    .filter(item =>
      Number.isInteger(item.paragraphIndex) &&
      Number.isInteger(item.startOffset) &&
      Number.isInteger(item.endOffset) &&
      item.endOffset > item.startOffset
    )
}

const normalizeItems = (value) => {
  const source = Array.isArray(value) ? value : jsonParse(value || '[]', [])
  if (!Array.isArray(source)) {
    return []
  }

  return source.map((item, index) => {
    const sourceRanges = normalizeSourceRanges(item?.sourceRanges)
    const paragraphIndices = Array.from(new Set(
      sourceRanges
        .map(range => Number(range.paragraphIndex))
        .filter(value => Number.isInteger(value))
    ))

    return {
      id: item?.id || `feature-${Date.now()}-${index}`,
      code: `F${index + 1}`,
      content: item?.content || '',
      sourceRanges,
      paragraphIndices,
    }
  })
}

const renumberItems = (source = []) => {
  return source.map((item, index) => ({
    ...item,
    code: `F${index + 1}`,
  }))
}

const syncStepState = (docResult, extOne = localExtOne.value) => {
  const nextStep2Info = {
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [componentTypeCode]: {
        ...(writingStore.step2Info?.step2Component?.[componentTypeCode] || {}),
        ...(props.data || {}),
        docResult,
        extOne,
      }
    }
  }

  writingStore.setStep2Info(nextStep2Info)
}

const dispatchFocus = (item) => {
  activeItemId.value = item?.id || ''
  if (!item) {
    window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
      detail: {
        projectId: props.projectId,
        sourceRanges: [],
        clear: true,
      },
    }))
    return
  }

  const sourceRanges = item?.sourceRanges || []
  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: item?.id || '',
      sourceRanges,
    })
    return
  }
  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges,
    },
  }))
}

const persistItems = async (nextItems, showSuccess = false) => {
  const normalizedItems = renumberItems(nextItems)
  const payload = {
    disclosureProjectId: props.projectId,
    componentTypeCode,
    docResult: JSON.stringify(normalizedItems),
    extOne: localExtOne.value || props.data?.extOne || '',
  }

  await fetchSaveComponent(payload)
  items.value = normalizedItems
  syncStepState(payload.docResult, payload.extOne)

  if (showSuccess) {
    ElMessage.success('特征拆解已保存')
  }
}

const handleGenerate = async ({ auto = false } = {}) => {
  if (!props.projectId || generating.value) {
    return
  }

  generating.value = true
  try {
    const result = await fetchGenerateFeatureBreakdown(props.projectId)
    items.value = normalizeItems(result)
    syncStepState(JSON.stringify(items.value), localExtOne.value)
    dispatchFocus(null)
    if (!auto) {
      ElMessage.success('特征拆解生成完成')
    }
  } catch (error) {
    console.error('feature breakdown generate failed', error)
    ElMessage.error(auto ? '特征拆解自动生成失败' : '特征拆解生成失败')
  } finally {
    generating.value = false
  }
}

const tryAutoGenerate = async () => {
  const projectId = String(props.projectId || '')
  const annotationKey = annotationRaw.value || ''
  const autoGenerateKey = projectId && annotationKey ? `${projectId}::${annotationKey}` : ''

  if (!autoGenerateKey || props.readOnly || generating.value || !hasAnnotationContent.value || hasFeatureContent.value) {
    return
  }

  if (lastAutoGenerateKey.value === autoGenerateKey) {
    return
  }

  lastAutoGenerateKey.value = autoGenerateKey
  await handleGenerate({ auto: true })
}

const handleAdd = async () => {
  const nextItems = [
    ...items.value,
    {
      id: `feature-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      code: '',
      content: '',
      sourceRanges: [],
      paragraphIndices: [],
    }
  ]

  items.value = renumberItems(nextItems)

  try {
    await persistItems(items.value)
  } catch (error) {
    console.error('feature breakdown add failed', error)
    ElMessage.error('新增失败')
  }
}

const handleContentBlur = async () => {
  try {
    await persistItems(items.value)
  } catch (error) {
    console.error('feature breakdown save failed', error)
    ElMessage.error('特征拆解保存失败')
  }
}

const handleDelete = async (index) => {
  const nextItems = items.value.filter((_, itemIndex) => itemIndex !== index)
  try {
    await persistItems(nextItems, true)
    if (activeItemId.value && !nextItems.find(item => item.id === activeItemId.value)) {
      dispatchFocus(null)
    }
  } catch (error) {
    console.error('feature breakdown delete failed', error)
    ElMessage.error('删除失败')
  }
}

const handleMove = async (index, offset) => {
  const targetIndex = index + offset
  if (targetIndex < 0 || targetIndex >= items.value.length) {
    return
  }

  const nextItems = [...items.value]
  const [current] = nextItems.splice(index, 1)
  nextItems.splice(targetIndex, 0, current)

  try {
    await persistItems(nextItems, true)
  } catch (error) {
    console.error('feature breakdown reorder failed', error)
    ElMessage.error('排序失败')
  }
}

const handleSelect = (item) => {
  dispatchFocus(item)
}

const buildSourceSummary = (item) => {
  const paragraphIndices = Array.from(new Set(
    (item?.sourceRanges || [])
      .map(range => Number(range.paragraphIndex))
      .filter(value => Number.isInteger(value))
  ))

  if (!paragraphIndices.length) {
    return '未关联原文'
  }

  return `关联段落：${paragraphIndices.join('、')}`
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    items.value = normalizeItems(newValue)
    if (activeItemId.value && !items.value.find(item => item.id === activeItemId.value)) {
      dispatchFocus(null)
    }
  },
  { immediate: true }
)

watch(
  () => props.data?.extOne,
  (newValue) => {
    localExtOne.value = newValue || ''
  },
  { immediate: true }
)

watch(
  () => props.projectId,
  () => {
    lastAutoGenerateKey.value = ''
  },
  { immediate: true }
)

watch(
  () => [props.projectId, props.readOnly, annotationRaw.value, hasAnnotationContent.value, hasFeatureContent.value],
  async () => {
    await tryAutoGenerate()
  },
  { immediate: true }
)

onUnmounted(() => {
  dispatchFocus(null)
})
</script>

<style scoped lang="scss">
@import './common.scss';

.feature-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.feature-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
}

.feature-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-table-head,
.feature-row {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) 104px;
  gap: 8px;
}

.feature-table-head {
  align-items: center;
}

.header-cell {
  padding: 6px 8px;
  color: #6B7280;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  background: #F3F4F6;
  border-radius: 8px;
}

.feature-table-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.feature-row {
  align-items: flex-start;
  padding: 10px;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &.active {
    border-color: #0F766E;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
  }
}

.feature-code-col {
  white-space: nowrap;
}

.feature-code-value {
  color: #111827;
  font-weight: 600;
  line-height: 28px;
}

.feature-content-col {
  min-width: 0;
}

.feature-action-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
}

.feature-action {
  height: 24px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: #F3F4F6;
  color: #374151;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;

  &.danger {
    color: #B91C1C;
  }
}

.feature-source {
  margin-top: 8px;
  color: #0F766E;
  font-size: 12px;

  &.empty {
    color: #9CA3AF;
  }
}

.placeholder {
  :deep(.el-skeleton__item) {
    border-radius: 8px;
  }
}

:deep(.el-textarea__inner) {
  min-height: 56px !important;
}

@media (max-width: 1200px) {
  .feature-table-head,
  .feature-row {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .feature-action-list,
  .feature-table-head .feature-action-col {
    grid-column: 1 / -1;
  }

  .feature-action-list {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
